import { countries, CountryCode } from "@redotech/locale/countries";
import { useHandler } from "@redotech/react-util/hook";
import { BadgeRedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { RedoTagsDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-tags-dropdown-input";
import { Flex } from "@redotech/redo-web/flex";
import { memo, useMemo, useState } from "react";

export const CountrySelectionModal = memo(function CountrySelectionModal({
  selectedCountries,
  onCountriesSelected,
  onClose,
}: {
  selectedCountries: CountryCode[];
  onCountriesSelected: (countries: CountryCode[]) => void;
  onClose: () => void;
}) {
  const [localSelectedCountries, setLocalSelectedCountries] =
    useState<CountryCode[]>(selectedCountries);

  const countryOptions: BadgeRedoListItem<CountryCode>[] = useMemo(() => {
    return countries.map((country) => ({
      type: "badge",
      id: country.code,
      value: country.code,
      badge: { text: country.name, color: "gray" },
    }));
  }, []);

  const selectedOptions = useMemo(() => {
    return countryOptions.filter((option) =>
      localSelectedCountries.includes(option.value),
    );
  }, [countryOptions, localSelectedCountries]);

  const handleSelectionChange = useHandler(
    (items: BadgeRedoListItem<CountryCode>[]) => {
      setLocalSelectedCountries(items.map((item) => item.value));
    },
  );

  const handleSave = useHandler(() => {
    onCountriesSelected(localSelectedCountries);
  });

  return (
    <RedoModal
      modalSize="md"
      onModalCloseRequested={onClose}
      primaryButton={{
        text: "Save",
        onClick: handleSave,
        disabled:
          localSelectedCountries.length === 0
            ? "Must select at least one country"
            : undefined,
      }}
      secondaryButton={{ text: "Cancel", onClick: onClose }}
      title="Select Countries"
    >
      <Flex dir="column" gap="lg" p="md">
        <RedoTagsDropdownInput<CountryCode>
          label="Countries"
          options={countryOptions}
          placeholder="Select countries"
          selectedOptions={selectedOptions}
          setSelectedOptions={handleSelectionChange}
          size="xs"
        />
      </Flex>
    </RedoModal>
  );
});
